import { Injectable } from '@angular/core';
import {
  ALWAYS_OPT_IN_MODULES,
  CLUB_MODULES,
  FEDERATION_MODULES,
  PLAN_MODULE_SLOTS,
  PaymentMethod,
  SPORT_LABELS_MAP,
  SportLabels,
  TenantConfig,
  TenantModule,
  TenantPlan,
} from '@models';
import {
  Observable,
  filter,
  first,
  map,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import { TenantContextService } from './tenant-context.service';
import { TenantFirebaseService } from './tenant-firebase.service';
import { TenantsService } from './tenants.service';

/**
 * TenantFeaturesService — determines which modules are active for the current
 * tenant based on their plan type and selected modules.
 *
 * Key design:
 *  - `tenantType$`    → derived from JWT claims (instant, no Firestore)
 *  - `tenantConfig$`  → full config from Firestore (shared/cached with shareReplay)
 *  - `hasModule$(m)`  → module gate, emits false until config loads then re-evaluates
 */
@Injectable({ providedIn: 'root' })
export class TenantFeaturesService {
  /**
   * Tenant type ('federation' | 'club') from JWT claims — available immediately
   * after auth, no Firestore call needed.
   */
  readonly tenantType$: Observable<'federation' | 'club' | null>;

  /**
   * Full tenant config document. Shared/cached — only one Firestore subscription
   * per tenant session. Emits null before config loads.
   */
  readonly tenantConfig$: Observable<TenantConfig | null>;

  /** All modules that are currently active (plan-included or explicitly selected). */
  readonly activeModules$: Observable<TenantModule[]>;

  /** How many additional modules the tenant can still activate (null = unlimited). */
  readonly availableSlots$: Observable<number | null>;

  /**
   * Payment methods enabled for the current tenant.
   * - `CARD` → online gateway (Stripe/Redsys) is configured and enabled
   * - `BANK` → bank transfer is enabled
   * Emits [] while config is loading, updates when config changes.
   */
  readonly availablePaymentMethods$: Observable<PaymentMethod[]>;

  /**
   * Active online payment gateway for the current tenant.
   * Emits 'none' while config loads or if no gateway is configured.
   */
  readonly paymentGateway$: Observable<'stripe' | 'redsys' | 'none'>;

  /**
   * Sport-specific UI labels derived from `TenantConfig.sport`.
   * Use `sportLabels$.facility` / `sportLabels$.facilities` to get the tenant-specific
   * name for the reservations module (e.g. "Instalación" for frontenis, "Barco" for remo).
   * Falls back to 'other' labels while config loads.
   */
  readonly sportLabels$: Observable<SportLabels>;

  /**
   * IBAN configured for bank transfer payments. Emits `null` when bank transfer
   * is not enabled or no IBAN has been set. Use this to show/copy the IBAN
   * in member-facing UIs.
   */
  readonly bankTransferIban$: Observable<string | null>;

  /**
   * The month (0-indexed) at which the sports season changes for this tenant.
   * Reads from `tenants/{tenantId}/settings/season`. Emits 6 (July) as default.
   */
  readonly seasonChangeMonth$: Observable<number>;

  constructor(
    private readonly tenantContext: TenantContextService,
    private readonly tenantsService: TenantsService,
    private readonly tenantFirebase: TenantFirebaseService,
  ) {
    // Tenant type from JWT claims — fast, no Firestore dependency
    this.tenantType$ = this.tenantContext.claimsOrNull$.pipe(
      map((c) => c?.tenantType ?? null),
    );

    // Firestore config — one subscription shared across all consumers
    this.tenantConfig$ = this.tenantContext.tenantId$.pipe(
      switchMap((id) => this.tenantsService.get(id).pipe(startWith(null))),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.seasonChangeMonth$ = this.tenantContext.tenantId$.pipe(
      switchMap((id) =>
        this.tenantsService.getSeasonSettings(id).pipe(
          map((s) => s?.seasonChangeMonth ?? 6),
          startWith(6),
        ),
      ),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.activeModules$ = this.tenantConfig$.pipe(
      map((cfg) => (cfg ? this._resolveActiveModules(cfg) : [])),
    );

    this.availableSlots$ = this.tenantConfig$.pipe(
      map((cfg) => {
        if (!cfg) return null;
        const slots = PLAN_MODULE_SLOTS[cfg.plan];
        if (slots === 'unlimited') return null;
        // Only selected (premium) modules count against the slot quota
        const premiumUsed = (cfg.modules ?? []).length;
        return Math.max(0, slots - premiumUsed);
      }),
    );

    this.availablePaymentMethods$ = this.tenantConfig$.pipe(
      filter((cfg): cfg is TenantConfig => cfg !== null),
      map((cfg) => {
        const methods: PaymentMethod[] = [];
        const { gateway, gatewayEnabled, bankTransferEnabled } =
          cfg.paymentConfig;
        if (gateway !== 'none' && gatewayEnabled === true) {
          methods.push(PaymentMethod.CARD);
        }
        if (bankTransferEnabled === true) {
          methods.push(PaymentMethod.BANK);
        }
        return methods;
      }),
    );

    this.paymentGateway$ = this.tenantConfig$.pipe(
      map((cfg) => {
        const gateway = cfg?.paymentConfig?.gateway;
        if (gateway === 'stripe' || gateway === 'redsys') return gateway;
        return 'none';
      }),
      startWith('none' as const),
    );

    this.bankTransferIban$ = this.tenantConfig$.pipe(
      map((cfg) => {
        if (!cfg?.paymentConfig?.bankTransferEnabled) return null;
        return cfg.paymentConfig.iban ?? null;
      }),
      startWith(null),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.sportLabels$ = this.tenantConfig$.pipe(
      map((cfg) => SPORT_LABELS_MAP[cfg?.sport ?? 'other']),
      startWith(SPORT_LABELS_MAP['other']),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    // Enterprise Firebase: switch to the tenant's own project when configured.
    this.tenantConfig$
      .pipe(
        filter((c): c is TenantConfig => c !== null),
        first(),
      )
      .subscribe((config) => {
        if (config.firebaseClientConfig) {
          this.tenantFirebase.switchToProject(
            config.firebaseClientConfig,
            config.firebaseProjectId,
          );
        }
      });
  }

  /**
   * Returns an Observable<boolean> that emits false immediately (while config
   * loads) and true/false once the config resolves.
   */
  hasModule$(module: TenantModule): Observable<boolean> {
    return this.activeModules$.pipe(map((mods) => mods.includes(module)));
  }

  /**
   * Synchronous check — only valid once tenantConfig$ has emitted a non-null value.
   */
  hasModule(module: TenantModule, config: TenantConfig): boolean {
    return this._resolveActiveModules(config).includes(module);
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  private _resolveActiveModules(config: TenantConfig): TenantModule[] {
    const plan: TenantPlan = config.plan;
    if (this._isUnlimitedPlan(plan)) {
      const allModules =
        config.type === 'federation'
          ? [...FEDERATION_MODULES]
          : [...CLUB_MODULES];
      const disabled = config.disabledModules ?? [];
      const selected = config.modules ?? [];
      // Opt-in modules are never auto-granted — require explicit selection
      return allModules
        .filter((m) => !ALWAYS_OPT_IN_MODULES.includes(m))
        .filter((m) => !disabled.includes(m))
        .concat(selected.filter((m) => ALWAYS_OPT_IN_MODULES.includes(m)));
    }
    // Union of plan-included modules (no slot cost) + tenant-selected premium modules
    const included = config.includedModules ?? [];
    const selected = config.modules ?? [];
    return [...new Set([...included, ...selected])];
  }

  private _isUnlimitedPlan(plan: TenantPlan): boolean {
    return plan === 'federation' || plan === 'business';
  }
}
